import PropTypes from 'prop-types';
import { useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios';
import { startPresenceHeartbeat, stopPresenceHeartbeat } from 'src/utils/presence';
// redux
import { store } from 'src/redux/store';
import { setCompanies, setSelectedCompany } from 'src/redux/slices/company';
//
import { AuthContext } from './auth-context';
import { isValidToken, setSession } from './utils';

function decodeToken(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(window.atob(base64).split('').map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`).join('')));
  } catch { return {}; }
}

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  user: null,
  loading: true,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN' ||
      action.type === 'ACCOUNT' ||
      action.type === 'REGISTER'
    ) {
    return {
      ...state,
      user: action.payload.user,
    };
  }

  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        // Fetch user and companies in parallel
        const [userResponse, companiesResponse] = await Promise.all([
          axios.get(API_ENDPOINTS.auth.me),
          axios.get(API_ENDPOINTS.auth.userCompanies).catch(() => ({ data: { companies: [] } })),
        ]);

        const { user } = userResponse.data;
        const companiesList = companiesResponse.data?.companies || [];

        // Always refresh companies in Redux from server
        store.dispatch(setCompanies(companiesList));

        // Select the company matching the JWT's primary company_id so that
        // role_name is correct for the user's actual workspace.
        const decoded = decodeToken(accessToken);
        const primaryCompanyId = decoded.company_id;
        const currentCompany = companiesList.find(c => c.id === primaryCompanyId) || companiesList[0] || null;
        if (currentCompany) {
          store.dispatch(setSelectedCompany(currentCompany));
        }

        dispatch({
          type: 'INITIAL',
          payload: {
            user,
          },
        });
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const data = {
      email,
      password,
    };

    const response = await axios.post(API_ENDPOINTS.auth.login, data);

    const { token, user, defaultPath, companies } = response.data;

    setSession(token);

    // Store all companies in Redux
    const companiesList = companies || [];
    store.dispatch(setCompanies(companiesList));

    // Select the company matching the JWT's primary company_id
    const storedToken = localStorage.getItem(STORAGE_KEY);
    const decoded = storedToken ? decodeToken(storedToken) : {};
    const primaryCompanyId = decoded.company_id;
    const defaultCompany = companiesList.find(c => c.id === primaryCompanyId) || companiesList[0] || null;
    if (defaultCompany) {
      store.dispatch(setSelectedCompany(defaultCompany));
    }

    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    });

    // Return defaultPath + companies for redirect logic
    return { defaultPath, companies: companiesList };
  }, []);

  // REGISTER
  const register = useCallback(async (email, password, firstname, lastname, password2) => {
    const data = {
      email,
      password,
      firstname,
      lastname,
      password2
    };

    const response = await axios.post(API_ENDPOINTS.auth.register, data);

    const { accessToken, user } = response.data;

    localStorage.setItem(STORAGE_KEY, accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  }, []);
  //UPDATE
  const update = useCallback(async(data)=>{
    const response = await axios.post(API_ENDPOINTS.auth.update, data);
    const { user } = response.data;
    // Only update auth state if server returns a refreshed user object.
    // Skipping the dispatch when user is absent prevents an accidental logout
    // caused by setting user to undefined in the reducer.
    if (user) {
      dispatch({
        type: 'ACCOUNT',
        payload: { user },
      });
    }
  })
  // LOGOUT
  const logout = useCallback(async () => {
    stopPresenceHeartbeat();
    setSession(null);
    store.dispatch(setCompanies([]));
    store.dispatch(setSelectedCompany(null));
    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  // Online/offline presence (RTDB). When we have an authenticated app
  // user, fetch a Firebase custom token from /api/auth/firebase-token,
  // sign into Firebase Auth so RTDB rules pass, and start the
  // `.info/connected` + onDisconnect tracker for /status/{userId}.
  // The aliased names (startPresenceHeartbeat / stopPresenceHeartbeat)
  // are kept for backwards-compat — under the hood they now drive the
  // RTDB-based tracker, not the prior Firestore heartbeat.
  useEffect(() => {
    const userId = state.user?.id;
    if (userId) {
      startPresenceHeartbeat(userId);
    } else {
      stopPresenceHeartbeat();
    }
    return () => {
      // Next effect tick / unmount stops the tracker via the else branch
      // above when state.user is cleared.
    };
  }, [state.user?.id]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      register,
      update,
      logout,
      initialize,
    }),
    [login, logout, register, initialize, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
